import { ButtonProps } from "@evidenceb/athena-common/design-system/Button";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import React, { PropsWithChildren, useContext, useState } from "react";
import { defineMessages, IntlShape, useIntl } from "react-intl";
import { Item, useOverlayTriggerState } from "react-stately";
import PageHeader from "../../../../../components/PageHeader/PageHeader";
import Tabs from "../../../../../design-system-components/Tabs/Tabs";
import { Pagetype } from "../../../../../interfaces/Config";
import { useUrl } from "../../../../../hooks/usePage";
import { sessionStore } from "../../../../../contexts/SessionContext";
import { LegacyLoader } from "../../../../../components/Loader/Loader";
import { Assignment, Playlist, PlaylistItem, PlaylistStatus } from "../../type";
import usePlaylistAPIClient from "../../api/usePlaylistAPIClient";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import queryKeys from "../../api/queryKeys";
import PlaylistDetail from "./Components/PlaylistDetail/PlaylistDetail";
import PlaylistItemContent from "./Components/PlaylistTabItem/PlaylistTabItem";
import { commonMessages, pageTitles } from "../../../../../utils/messages";
import RouteWithHeaderWrapper from "../../../../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import { authorizedItemType, filterWithStatus } from "../utils";
import {
    Workmode,
    WorkmodeGrid,
    WorkmodePage,
} from "../../../../../design-system-components/WorkmodeLayout";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";
import { usePlaylistQuery } from "../../api/useQueries";
import BottomSheetPreview from "./Components/BottomSheetPreview/BottomSheetPreview";

import "./TeacherPlaylistModeList.scss";
import { ROUTES } from "../../../../../design-system-components/WorkmodeLayout/const";
import { playlistMessages } from "../EditPlaylist/playlistMessages";
import { useImages } from "../../../../../hooks/useConfig";
import EmptyContainer from "../../../../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { defaultImages } from "../../../../../utils/defaultImages";

const TeacherPlaylistModeList = () => {
    const { isTabletOrSmaller } = useBreakpoints();
    const isOnline = useOnlineStatus();
    const intl = useIntl();
    const baseurl = useUrl(Pagetype.WORKMODE_PLAYLIST);
    const {
        session: { userId },
    } = useContext(sessionStore);
    const tutosFlag = useFeatureFlag("tutosInPlaylist");
    const workshopsFlag = useFeatureFlag("workshopsInPlaylist");

    const [playlistInPreviewId, setPlaylistInPreviewId] = useState<string>();
    const { data: playlistInPreview } = usePlaylistQuery(
        playlistInPreviewId ?? "",
        {
            enabled: !!playlistInPreviewId,
        }
    );

    const { getPlaylists, getAssignments } = usePlaylistAPIClient();
    const playlistsQuery = useQuery({
        queryKey: queryKeys.getPlaylists({ author_id: userId }),
        queryFn: getPlaylists,
        staleTime: Infinity,
    });

    const assignmentsQuery = useQuery({
        queryKey: queryKeys.getAssignments({ teacher: userId }),
        queryFn: getAssignments,
        staleTime: Infinity,
    });

    const playlistPreviewModalState = useOverlayTriggerState({});

    const updatePreview = (id: string) => {
        if (!isTabletOrSmaller && playlistInPreviewId === id) {
            setPlaylistInPreviewId(undefined);
        } else {
            setPlaylistInPreviewId(id);
        }
        if (isTabletOrSmaller) playlistPreviewModalState.open();
    };

    const images = useImages();
    // TODO error handling
    if (playlistsQuery.isError) return <LegacyLoader />;

    const createButtonProps: ButtonProps = {
        label: intl.formatMessage(messages.playlistCreate),
        icons: [
            {
                placement: "left",
                path: "add",
            },
        ],
        asRouterLink: {
            to: `${baseurl}/${ROUTES.create}`,
        },
    };
    return (
        <Wrapper>
            {isTabletOrSmaller && isOnline && (
                <Workmode.CreateResourceItem
                    label={intl.formatMessage(messages.playlistCreate)}
                    createPageUrl={`${baseurl}/create/`}
                />
            )}
            <WorkmodeGrid>
                <WorkmodeGrid.LeftColumn id="teacher-playlist-mode-list__left-column">
                    <Tabs
                        variant="accent"
                        className="teacher-playlist-mode-list__tabs"
                        onSelectionChange={() => {
                            setPlaylistInPreviewId(undefined);
                        }}
                        defaultSelectedKey={PlaylistStatus.DRAFT}
                    >
                        <Item
                            title={getPlaylistTabTitle(
                                PlaylistStatus.DRAFT,
                                intl,
                                playlistsQuery.data
                                    ?.filter(
                                        filterWithStatus(PlaylistStatus.DRAFT)
                                    )
                                    .filter(
                                        authorizedItemType(
                                            tutosFlag,
                                            workshopsFlag
                                        )
                                    ).length
                            )}
                            key={PlaylistStatus.DRAFT}
                        >
                            <PlaylistItemContent
                                emptyContainerMain={{
                                    img: images?.resources?.teacher?.playlists
                                        ?.draft,
                                    defaultText: intl.formatMessage(
                                        messages.playlistDraftMainPlaceholder
                                    ),
                                    button: createButtonProps,
                                }}
                                onPlaylistClick={updatePreview}
                                status={PlaylistStatus.DRAFT}
                                playlistInPreview={playlistInPreview}
                                onPlaylistInPreviewBlur={() =>
                                    setPlaylistInPreviewId(undefined)
                                }
                            />
                        </Item>
                        <Item
                            title={getPlaylistTabTitle(
                                PlaylistStatus.SENT,
                                intl,
                                playlistsQuery.data
                                    ?.filter(
                                        filterWithStatus(PlaylistStatus.SENT)
                                    )
                                    .filter(
                                        authorizedItemType(
                                            tutosFlag,
                                            workshopsFlag
                                        )
                                    ).length
                            )}
                            key={PlaylistStatus.SENT}
                        >
                            <PlaylistItemContent
                                emptyContainerMain={{
                                    img: images?.resources?.teacher?.playlists
                                        ?.sent,
                                    defaultText: intl.formatMessage(
                                        messages.playlistSentMainPlaceholder
                                    ),
                                    button: createButtonProps,
                                }}
                                onPlaylistClick={updatePreview}
                                status={PlaylistStatus.SENT}
                                playlistInPreview={playlistInPreview}
                                onPlaylistInPreviewBlur={() =>
                                    setPlaylistInPreviewId(undefined)
                                }
                            />
                        </Item>
                        <Item
                            title={getPlaylistTabTitle(
                                PlaylistStatus.ARCHIVED,
                                intl,
                                playlistsQuery.data
                                    ?.filter(
                                        filterWithStatus(
                                            PlaylistStatus.ARCHIVED
                                        )
                                    )
                                    .filter(
                                        authorizedItemType(
                                            tutosFlag,
                                            workshopsFlag
                                        )
                                    ).length
                            )}
                            key={PlaylistStatus.ARCHIVED}
                        >
                            <PlaylistItemContent
                                emptyContainerMain={{
                                    img: images?.resources?.teacher?.playlists
                                        ?.archived,
                                    defaultText: intl.formatMessage(
                                        messages.playlistArchivedMainPlaceholder
                                    ),
                                    button: createButtonProps,
                                }}
                                onPlaylistClick={updatePreview}
                                status={PlaylistStatus.ARCHIVED}
                                playlistInPreview={playlistInPreview}
                                onPlaylistInPreviewBlur={() =>
                                    setPlaylistInPreviewId(undefined)
                                }
                            />
                        </Item>
                    </Tabs>
                </WorkmodeGrid.LeftColumn>
                <WorkmodeGrid.RightColumn
                    className="teacher-playlist-mode-list__side-container"
                    createResourceItemBtn={{
                        url: `${baseurl}/${ROUTES.create}`,
                        label: intl.formatMessage(messages.playlistCreate),
                    }}
                >
                    {playlistInPreview && assignmentsQuery.data ? (
                        <RightColumnContent
                            playlistInPreview={playlistInPreview}
                            baseurl={baseurl}
                            assignmentsQuery={assignmentsQuery}
                        />
                    ) : (
                        <div className="teacher-playlist-mode-list__no-selection">
                            <EmptyContainer
                                img={
                                    images?.resources?.teacher?.playlists
                                        ?.rightColumn
                                }
                                defaultImg={
                                    defaultImages.teacherResourcesPlaylistsRightColumn
                                }
                            >
                                <p>
                                    {intl.formatMessage(
                                        messages.playlistSidePlaceholder
                                    )}
                                </p>
                            </EmptyContainer>
                        </div>
                    )}
                </WorkmodeGrid.RightColumn>
            </WorkmodeGrid>

            {isTabletOrSmaller && (
                <BottomSheetPreview
                    state={playlistPreviewModalState}
                    playlistId={playlistInPreviewId}
                    nbPlaylists={playlistsQuery.data?.length}
                    onClearPreview={() => setPlaylistInPreviewId(undefined)}
                />
            )}
        </Wrapper>
    );
};
export default TeacherPlaylistModeList;

const Wrapper = ({ children }: PropsWithChildren) => {
    const intl = useIntl();
    const title = intl.formatMessage(pageTitles.teacherPlaylistsList);
    const workModeSelectionUrl = useUrl(Pagetype.WORK_MODES);
    return (
        <>
            <TabTitle title={title} />
            <RouteWithHeaderWrapper routerClassName="teacher-playlist-mode-list__router">
                <WorkmodePage>
                    <PageHeader
                        title={{ content: title }}
                        backTo={workModeSelectionUrl}
                    />
                    {children}
                </WorkmodePage>
            </RouteWithHeaderWrapper>
        </>
    );
};

interface RightColumnContentProps {
    playlistInPreview: Playlist<PlaylistItem>;
    baseurl: string;
    assignmentsQuery: UseQueryResult<Assignment[], unknown>;
}
const RightColumnContent = ({
    playlistInPreview,
    baseurl,
    assignmentsQuery,
}: RightColumnContentProps) => {
    const intl = useIntl();
    const isDraft = playlistInPreview.status.status === PlaylistStatus.DRAFT;
    return (
        <>
            <Workmode.ResourcePreview
                className="teacher-playlist-mode-list__preview__resource-preview"
                title={playlistInPreview.name}
                description={playlistInPreview.description}
                previewUrl={`${baseurl}/${ROUTES.seePlaylist}/${playlistInPreview.learning_set_id}`}
            />
            <Workmode.AddAssignment
                className="teacher-playlist-mode-list__preview__add-assignement"
                title={intl.formatMessage(playlistMessages.recipients)}
                addButton={{
                    label: isStudents(playlistInPreview, assignmentsQuery)
                        ? intl.formatMessage(commonMessages.modify)
                        : intl.formatMessage(commonMessages.add),
                    ariaLabel: isStudents(playlistInPreview, assignmentsQuery)
                        ? intl.formatMessage(messages.playlistModifyRecipients)
                        : intl.formatMessage(messages.playlistAddRecipients),
                }}
                assignmentUrl={`${baseurl}/${
                    isDraft ? ROUTES.edit : ROUTES.editPlaylistRecipients
                }/${playlistInPreview.learning_set_id}${
                    isDraft ? "?isEditDraftPlaylistAssignement=true" : ""
                }`}
                hideEditButton={
                    playlistInPreview.status.status === PlaylistStatus.ARCHIVED
                }
            />
            <PlaylistDetail playlist={playlistInPreview} />
        </>
    );
};

const isStudents = (
    playlistInPreview: Playlist<PlaylistItem>,
    assignmentsQuery: UseQueryResult<Assignment[], unknown>
) => {
    const resource = assignmentsQuery.data!.find(
        (a) =>
            a.assigned_resource?.resource_id ===
            playlistInPreview.learning_set_id
    );
    return !!resource?.students.length;
};

const getPlaylistTabTitle = (
    status: Playlist["status"]["status"],
    intl: IntlShape,
    playlistsNb: number = 0
) => {
    let label;
    switch (status) {
        case PlaylistStatus.SENT: {
            label = messages.playlistSentTitle;
            break;
        }
        case PlaylistStatus.DRAFT: {
            label = messages.playlistDraftTitle;
            break;
        }
        case PlaylistStatus.ARCHIVED: {
            label = messages.playlistArchivedTitle;
            break;
        }
        default: {
            const _: never = status;
            throw new Error("Unkown playlist status");
        }
    }

    label = `${intl.formatMessage(label)} (${playlistsNb})`;
    return label;
};

const messages = defineMessages({
    playlistSentTitle: {
        id: "playlist-sent-title",
        defaultMessage: "Sent playlists",
    },
    playlistSentMainPlaceholder: {
        id: "playlist-active-main-placeholder",
        defaultMessage:
            "Create playlists from our resources and share it with your students !",
    },
    playlistArchivedTitle: {
        id: "playlist-archives-title",
        defaultMessage: "Archives",
    },
    playlistArchivedMainPlaceholder: {
        id: "playlist-archive-main-placeholder",
        defaultMessage:
            "You can archive compilations that you no longer use. Archived compilations will no longer be available to students.",
    },
    playlistDraftTitle: {
        id: "playlist-draft-title",
        defaultMessage: "Draft playlists",
    },
    playlistDraftMainPlaceholder: {
        id: "playlist-draft-main-placeholder",
        defaultMessage:
            "Your playlists are automatically saved in your drafts until you send them to your students.",
    },
    playlistSidePlaceholder: {
        id: "playlist-side-placeholder",
        defaultMessage:
            "Select a playlist to get an overview of its recipients and their progress.",
    },
    playlistDetails: {
        id: "playlist-details",
        defaultMessage: "Playlist details",
    },
    playlistCreate: {
        id: "playlist-create",
        defaultMessage: "Create a playlist.",
    },
    playlistAddRecipients: {
        id: "playlist-playlistAddRecipients",
        defaultMessage: "Add recipients",
    },
    playlistModifyRecipients: {
        id: "playlist-playlistModifyRecipients",
        defaultMessage: "Modify recipients",
    },
});
